import cn from 'classnames';
import { useIntl } from 'react-intl';
import { BoxV2, Tag } from 'sonata-react-components';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { getRetargetingEventLabel } from '../../RetargetingShortcut/getRetargetingEventLabel.helper';
import { BoxGroupStyled, OrAndStyled } from './BoxGroup.styled';

import type { ReactNode } from 'react';
import type { RetargetingSegmentType } from 'sonata-static-storage/lib/schemas/targeting/Audience/Audience.types';
import type { AudienceGroup } from '../../AudienceTargeting';

type Props = {
  group: AudienceGroup;
  groupNumber: number;
  index: number;
  isGroupActive: boolean;
  onChangeDeleteGroup: (id: string) => void;
  onChangeDeleteRetargetingSegment?: () => void;
  onChangeDeleteSegment: (id: string, groupId: string) => void;
  onSelectedGroup: (id: string) => void;
  retargetingSegment?: RetargetingSegmentType | null;
};

type SegmentCardProps = {
  category?: string;
  country?: string;
  deleteButtonId: string;
  name: string;
  onDelete?: () => void;
  provider: string;
  tag?: ReactNode;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const getCountry = (countries: string[]) => {
  return countries.map(c => {
    const country = worldCountries.find(wc => wc.cca2.toLowerCase() === c);
    return country?.name.common || 'Unknown Country';
  });
};

function BoxGroup({
  group,
  groupNumber,
  index,
  isGroupActive,
  onChangeDeleteGroup,
  onChangeDeleteRetargetingSegment,
  onChangeDeleteSegment,
  onSelectedGroup,
  retargetingSegment
}: Props) {
  const { formatMessage } = useIntl();
  const segmentsCount = group.segmentIds.length + (retargetingSegment ? 1 : 0);
  const isExcluded = group.type === 'excluded';

  const countryLabel = formatMessage({
    defaultMessage: 'Country:',
    id: 'targeting.audienceTargeting.audienceSettings.group.segment.country'
  });
  const providerLabel = formatMessage({
    defaultMessage: 'Provider:',
    id: 'targeting.audienceTargeting.audienceSettings.group.segment.provider'
  });
  const deleteSegmentLabel = formatMessage({
    defaultMessage: 'Remove segment',
    id: 'targeting.audienceTargeting.audienceSettings.group.segment.delete'
  });

  function SegmentCard({ category, country, deleteButtonId, name, onDelete, provider, tag }: SegmentCardProps) {
    return (
      <div className="boxGroup__segment">
        <div className="boxGroup__segmentContent">
          <div className="boxGroup__segmentTitle" title={category ? `${name} -${category}` : name}>
            <span className="boxGroup__segmentName">{name}</span>
            {category && <span className="boxGroup__segmentCategory">-{category}</span>}
          </div>
          <div className="boxGroup__segmentInfo">
            {country && (
              <>
                <span>
                  <strong>{countryLabel}</strong> {country}
                </span>
                <span className="boxGroup__segmentInfoSeparator" />
              </>
            )}
            <span>
              <strong>{providerLabel}</strong> {provider}
            </span>
          </div>
        </div>
        {tag}
        <button
          aria-label={deleteSegmentLabel}
          className="boxGroup__deleteSegment"
          id={deleteButtonId}
          onClick={onDelete}
          title={deleteSegmentLabel}
          type="button"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>
    );
  }

  function OrAndComponent({ show, type }: { show: boolean; type: string }) {
    if (!show) {
      return null;
    }
    return (
      <OrAndStyled className="orAndContainer">
        <div className="orAndContainer__line" />
        <div className="orAndContainer__orAndText">
          {formatMessage({
            defaultMessage: type,
            id: `targeting.audienceTargeting.audienceSettings.group.orAnd.${type}`
          })}
        </div>
        <div className="orAndContainer__line" />
      </OrAndStyled>
    );
  }

  return (
    <BoxGroupStyled className="boxGroup">
      <OrAndComponent show={index !== 0} type={isExcluded ? 'and' : 'or'} />
      <BoxV2 className="boxGroup__box" id={`boxGroup-${group.id}`}>
        <BoxV2.Header
          bgColor="boxBgColor01"
          className="boxGroup__header"
          isActive={isGroupActive}
          isSelectable={!isExcluded}
          onActiveChange={() => onSelectedGroup(group.id)}
          padding="8px 16px"
          selectablePosition="left"
          selectableType="radio"
          separatorColor="tableBorderRow"
        >
          <BoxV2.Header.Title
            title={
              isExcluded
                ? formatMessage({
                    defaultMessage: 'Excluded group',
                    id: 'targeting.audienceTargeting.audienceSettings.group.name.excluded'
                  })
                : formatMessage(
                    {
                      defaultMessage: 'Group {groupNumber}',
                      id: 'targeting.audienceTargeting.audienceSettings.group.name.included'
                    },
                    { groupNumber }
                  )
            }
            titleFontStyles="boxGroup__title"
          />
          <BoxV2.Header.AdditionalContentRight>
            <div className="boxGroup__headerRight">
              <span className="boxGroup__segmentsCounter">
                {formatMessage(
                  {
                    defaultMessage: '(<b>{segmentsCounter}</b> Segments)',
                    id: 'targeting.audienceTargeting.audienceSettings.group.segmentsCounter'
                  },
                  { b: (chunks: ReactNode[]) => <strong>{chunks}</strong>, segmentsCounter: segmentsCount }
                )}
              </span>
              <Tag id={`boxGroup-${group.id}-tag`} status={isExcluded ? 'wrong' : 'ok'}>
                {isExcluded
                  ? formatMessage({
                      defaultMessage: 'Exclude',
                      id: 'targeting.audienceTargeting.audienceSettings.group.tag.exclude'
                    })
                  : formatMessage({
                      defaultMessage: 'Include',
                      id: 'targeting.audienceTargeting.audienceSettings.group.tag.include'
                    })}
              </Tag>
            </div>
          </BoxV2.Header.AdditionalContentRight>
        </BoxV2.Header>
        <BoxV2.Body className="boxGroup__body" padding="8px">
          {segmentsCount === 0 ? (
            <div className="boxGroup__emptyState">
              {formatMessage({
                defaultMessage: 'Add segments to the group',
                id: 'targeting.audienceTargeting.audienceSettings.group.emptyGroupSegments'
              })}
            </div>
          ) : (
            <>
              <div className={cn('boxGroup__segmentContainer', { 'boxGroup__segmentContainer--scroll': isExcluded })}>
                {group.segments?.map(item => (
                  <SegmentCard
                    category={item.categories?.map((category: Record<string, any>) => category.name).join(', ')}
                    country={getCountry(item.countries).join(', ')}
                    deleteButtonId={`boxGroup-${group.id}-deleteSegment-${item.segmentId}`}
                    key={item.segmentId}
                    name={item.name}
                    onDelete={() => onChangeDeleteSegment(item.segmentId, group.id)}
                    provider={item.providers.map(capitalize).join(', ')}
                  />
                ))}
                {retargetingSegment && (
                  <SegmentCard
                    deleteButtonId={`boxGroup-${group.id}-deletePendingRetargeting`}
                    name={`Retargeting - ${retargetingSegment.campaignName} - ${getRetargetingEventLabel(formatMessage, retargetingSegment.eventType)}`}
                    onDelete={onChangeDeleteRetargetingSegment}
                    provider="TapTap Networks"
                    tag={
                      <Tag id={`boxGroup-${group.id}-pendingRetargetingTag`} status="warning">
                        {formatMessage({
                          defaultMessage: 'Pending · created when saved',
                          id: 'targeting.audienceTargeting.retargetingShortcut.pendingTag'
                        })}
                      </Tag>
                    }
                  />
                )}
              </div>
              <button
                className="boxGroup__deleteGroup"
                id={`boxGroup-${group.id}-deleteGroup`}
                onClick={() => onChangeDeleteGroup(group.id)}
                type="button"
              >
                <i className="fa-regular fa-trash-can" />
                {formatMessage({
                  defaultMessage: 'Delete group',
                  id: 'targeting.audienceTargeting.audienceSettings.group.deleteButton'
                })}
              </button>
            </>
          )}
        </BoxV2.Body>
      </BoxV2>
    </BoxGroupStyled>
  );
}

export default BoxGroup;
